import React, { useState } from "react";
import "../styles/pricing.css";

export default function Elite() {
  // Step control
  const [billingChoice, setBillingChoice] = useState(null); // null | "monthly" | "annual"
  const [formDone, setFormDone] = useState(false);
  const [ageVerified, setAgeVerified] = useState(null); // null | true | false

  // Monthly form state (MANDATORY FIELDS)
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Send state (same UX pattern as Explore/Adventure)
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState(null); // null | "success" | "error"

  // Step 0: billing choice
  const pickAnnual = () => {
    setBillingChoice("annual");
    setAgeVerified(null);
    setFormDone(false);
    setStatus(null);
  };

  const pickMonthly = () => {
    setBillingChoice("monthly");
    setAgeVerified(null); // age gate should show first
    setFormDone(false); // reset monthly step
    setStatus(null);
  };

  // Age gate actions
  const verifyYes = () => setAgeVerified(true);
  const verifyNo = () => setAgeVerified(false);

  const backToBilling = () => {
    setBillingChoice(null);
    setFormDone(false);
    setAgeVerified(null);
    setStatus(null);
  };

  // Monthly form submit -> SendGrid via /api/send-lead
  const onSubmitMonthlyForm = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setStatus(null);

    try {
      const payload = {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,

        // Include phone in the email
        phone: form.phone,

        // Helpful context for Sales
        interest: "elite",
        season: "monthly",
        vehicles: "Monthly membership request",

        tier: "elite",
        billingChoice: "monthly",
        source: "Elite page - Monthly flow",
      };

      const res = await fetch("/api/send-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Send failed:", res.status, data);
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      setStatus("success");
      setFormDone(true);
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="page">
      {/* HERO */}
      <section className="hero hero--pricing hero--explore">
        <div className="hero__overlay" />
        <div className="container hero__content hero__content--center">
          <h1 className="hero__title">Let The Adventure Begin!</h1>
          <p className="hero__subtitle">
            Simple membership. Full access. Zero ownership headaches.
          </p>
        </div>
      </section>

      {/* STEP 0: BILLING CHOICE (BEFORE AGE CHECK) */}
      {billingChoice === null && (
        <section className="section">
          <div className="container">
            <div className="ageGate">
              <div className="ageGate__eyebrow">Your Choice</div>
              <h2 className="ageGate__title">Choose Your Billing Option</h2>
              <p className="ageGate__text"></p>

              <div className="ageGate__choices">
                <button
                  className="billingChoice billingChoice--annual"
                  type="button"
                  onClick={pickAnnual}
                >
                  <div className="billingChoice__badge">Best Value</div>

                  <div className="billingChoice__title">Pay Annually</div>
                  <div className="billingChoice__price">One simple payment</div>

                  <div className="billingChoice__desc">
                    Pay once and you're set for the year! Maximum value.
                  </div>

                  <div className="billingChoice__cta">Continue with Annual</div>
                </button>

                <button
                  className="billingChoice billingChoice--monthly"
                  type="button"
                  onClick={pickMonthly}
                >
                  <div className="billingChoice__title">Pay Monthly</div>
                  <div className="billingChoice__price">
                    Spread your payments over 12 months
                  </div>

                  <div className="billingChoice__desc">
                    Prefer flexibility? Our monthly payment options delivers that!
                  </div>

                  <div className="billingChoice__cta">Continue with Monthly</div>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========================= */}
      {/* ANNUAL FLOW: AGE CHECK → CONTENT */}
      {/* ========================= */}
      {billingChoice === "annual" && (
        <>
          {/* AGE GATE */}
          <section className="section">
            <div className="container">
              {ageVerified === null && (
                <div className="ageGate">
                  <div className="ageGate__eyebrow">Before you continue</div>
                  <h2 className="ageGate__title">Confirm Your Eligibility</h2>
                  <p className="ageGate__text">
                    This experience is available to members 21 years of age or older.
                  </p>

                  <div className="ageGate__choices">
                    <button
                      className="billingChoice billingChoice--annual"
                      type="button"
                      onClick={verifyYes}
                    >
                      <div className="billingChoice__title">I am 21+</div>
                      <div className="billingChoice__desc">
                        Continue to your annual membership experience.
                      </div>
                      <div className="billingChoice__cta">Continue</div>
                    </button>

                    <button
                      className="billingChoice billingChoice--monthly"
                      type="button"
                      onClick={verifyNo}
                    >
                      <div className="billingChoice__title">I am under 21</div>
                      <div className="billingChoice__desc">
                        Unfortunately this program is only available to those 21 years
                        of age and older.
                      </div>
                      <div className="billingChoice__cta">Go back</div>
                    </button>
                  </div>

                  <div className="ageGate__actions" style={{ marginTop: 14 }}>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={backToBilling}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {ageVerified === false && (
                <div className="ageGate ageGate--blocked">
                  <div className="ageGate__eyebrow">Access Restricted</div>
                  <h2 className="ageGate__title">Not Eligible</h2>
                  <p className="ageGate__text">
                    You must be at least 21 years old to purchase a membership.
                  </p>

                  <div className="ageGate__actions">
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={() => setAgeVerified(null)}
                    >
                      Back
                    </button>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={backToBilling}
                    >
                      Choose billing option
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ANNUAL CONTENT (only after annual + age verified) */}
          {ageVerified === true && (
            <section className="section section--pricing">
              <div className="container pricingWrap">
                <h2 className="section__title" style={{ textAlign: "center" }}>
                  Welcome to Elite
                </h2>
                <p className="section__subtitle" style={{ textAlign: "center" }}></p>

                <div className="ageGate" style={{ marginTop: 24 }}>
                  <h3 className="ageGate__title" style={{ marginBottom: 10 }}>
                    Next Steps
                  </h3>

                  <div style={{ marginTop: 12 }}>
                    <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
                    <stripe-pricing-table
                      pricing-table-id="prctbl_1Spaq5Lf216OKSFEOOBOSDQp"
                      publishable-key="pk_live_51RRFEwLf216OKSFEIQFqn2yO6auDEr9BK4COHcSbk7imVYAr5qPJolMjZldMYVFoLKAUYbdbOmhVQbuULTrtuBew00fPpOnSZn"
                    ></stripe-pricing-table>
                  </div>

                  <div className="ageGate__actions" style={{ marginTop: 18 }}>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={backToBilling}
                    >
                      Start over
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* ========================= */}
      {/* MONTHLY FLOW: AGE CHECK → FORM → THANK YOU */}
      {/* ========================= */}
      {billingChoice === "monthly" && (
        <>
          {/* AGE GATE (BEFORE FORM) */}
          <section className="section">
            <div className="container">
              {ageVerified === null && (
                <div className="ageGate">
                  <div className="ageGate__eyebrow">Before you continue</div>
                  <h2 className="ageGate__title">Confirm Your Eligibility</h2>
                  <p className="ageGate__text">
                    This experience is available to members 21 years of age or older.
                  </p>

                  <div className="ageGate__choices">
                    <button
                      className="billingChoice billingChoice--annual"
                      type="button"
                      onClick={verifyYes}
                    >
                      <div className="billingChoice__title">I am 21+</div>
                      <div className="billingChoice__desc">
                        Continue to the monthly request form.
                      </div>
                      <div className="billingChoice__cta">Continue</div>
                    </button>

                    <button
                      className="billingChoice billingChoice--monthly"
                      type="button"
                      onClick={verifyNo}
                    >
                      <div className="billingChoice__title">I am under 21</div>
                      <div className="billingChoice__desc">
                        Unfortunately this program is only available to those 21 years
                        of age and older.
                      </div>
                      <div className="billingChoice__cta">Go back</div>
                    </button>
                  </div>

                  <div className="ageGate__actions" style={{ marginTop: 14 }}>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={backToBilling}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {ageVerified === false && (
                <div className="ageGate ageGate--blocked">
                  <div className="ageGate__eyebrow">Access Restricted</div>
                  <h2 className="ageGate__title">Not Eligible</h2>
                  <p className="ageGate__text">
                    You must be at least 21 years old to purchase a membership.
                  </p>

                  <div className="ageGate__actions">
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={() => setAgeVerified(null)}
                    >
                      Back
                    </button>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={backToBilling}
                    >
                      Choose billing option
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* MONTHLY FORM (only after age verified) */}
          {ageVerified === true && !formDone && (
            <section className="section">
              <div className="container">
                <div className="ageGate">
                  <div className="ageGate__eyebrow">Monthly Membership</div>
                  <h2 className="ageGate__title">Quick Request</h2>
                  <p className="ageGate__text">
                    Fill this out and we’ll confirm next steps before you finalize your membership.
                  </p>

                  <form
                    onSubmit={onSubmitMonthlyForm}
                    className="ctaForm"
                    aria-label="Monthly membership form"
                  >
                    <div className="ctaForm__grid">
                      {/* First Name */}
                      <div className="field">
                        <label className="field__label" htmlFor="m_firstName">
                          First Name
                        </label>
                        <input
                          id="m_firstName"
                          className="field__input"
                          type="text"
                          value={form.firstName}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, firstName: e.target.value }))
                          }
                          required
                        />
                      </div>

                      {/* Last Name */}
                      <div className="field">
                        <label className="field__label" htmlFor="m_lastName">
                          Last Name
                        </label>
                        <input
                          id="m_lastName"
                          className="field__input"
                          type="text"
                          value={form.lastName}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, lastName: e.target.value }))
                          }
                          required
                        />
                      </div>

                      {/* Email */}
                      <div className="field field--full">
                        <label className="field__label" htmlFor="m_email">
                          Email
                        </label>
                        <input
                          id="m_email"
                          className="field__input"
                          type="email"
                          value={form.email}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, email: e.target.value }))
                          }
                          required
                        />
                      </div>

                      {/* Phone */}
                      <div className="field field--full">
                        <label className="field__label" htmlFor="m_phone">
                          Phone Number
                        </label>
                        <input
                          id="m_phone"
                          className="field__input"
                          type="tel"
                          value={form.phone}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, phone: e.target.value }))
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className="ageGate__actions" style={{ marginTop: 16 }}>
                      <button
                        className="btn btn--primary"
                        type="submit"
                        disabled={isSending}
                      >
                        {isSending ? "Sending..." : "Continue"}
                      </button>
                      <button
                        className="btn btn--ghost"
                        type="button"
                        onClick={backToBilling}
                        disabled={isSending}
                      >
                        Back
                      </button>
                    </div>

                    {status === "success" && (
                      <p className="ctaForm__fineprint" role="status">
                        ✅ Request received — continuing.
                      </p>
                    )}

                    {status === "error" && (
                      <p className="ctaForm__fineprint" role="status">
                        ❌ Something went wrong. Please try again.
                      </p>
                    )}

                    {!status && (
                      <p className="ctaForm__fineprint">
                        Your request is sent directly to our concierge team.
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          )}

          {/* THANK YOU / CHECKOUT SECTION */}
          {ageVerified === true && formDone && (
            <section className="section section--pricing">
              <div className="container pricingWrap">
                <div className="ageGate" style={{ marginTop: 24 }}>
                  <h3 className="ageGate__title" style={{ marginBottom: 10 }}>
                    Monthly Checkout
                  </h3>
                  <p className="ageGate__text">
                    Thank you! Our concierge will be in touch.
                  </p>

                  {/* OPTIONAL: Add your MONTHLY stripe pricing table here later */}

                  <div className="ageGate__actions" style={{ marginTop: 18 }}>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={backToBilling}
                    >
                      Start over
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
